import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  Room,
  RemoteParticipant,
  RemoteTrackPublication,
  RemoteDataTrack,
  LocalVideoTrack,
  LocalDataTrack,
} from 'twilio-video';
import { takePrintScreen } from '../../utils/print';
import { useToast } from '../../components/ToastProvider/ToastProvider';

export default function useTakePrintScreen(room: Room | null) {
  // Pega os parâmetros da URL via hook React
  const params = useParams<{ URLModulo?: string; URLRoomName?: string }>();
  const { showToast } = useToast();

  useEffect(() => {
    if (!room) return;

    const dataTrackListeners = new Map<RemoteDataTrack, (msg: string) => void>();

    const handleDataTrack = (track: RemoteDataTrack) => {
      const onMessage = (msg: string) => {
        let data: any;

        try {
          data = JSON.parse(msg);
        } catch (e) {
          alert('[⚠️ onMessage] Mensagem recebida não é JSON:');
          return;
        }

        if (data.comando === 'tirar_foto') {
          const videoTrackPublication = Array.from(room.localParticipant.tracks.values()).find(
            publication => publication.track?.kind === 'video'
          );
          const videoTrack = videoTrackPublication?.track as LocalVideoTrack | undefined;

          const localDataTrackPublication = Array.from(room.localParticipant.tracks.values()).find(
            publication => publication.track?.kind === 'data'
          );
          const localDataTrack = localDataTrackPublication?.track as LocalDataTrack | undefined;

          if (videoTrack && videoTrack.isEnabled && localDataTrack) {
            takePrintScreen(videoTrack, localDataTrack, params);
          }
        }

        if (data.tipo === 'resposta_foto') {
          if (data.status === 'success') {
            showToast(data.mensagem || 'Foto enviada com sucesso!', 'success');
          } else {
            showToast(data.mensagem || 'Erro ao enviar a foto', 'error');
          }
        }
      };

      if (!dataTrackListeners.has(track)) {
        track.on('message', onMessage);
        dataTrackListeners.set(track, onMessage);
      }
    };

    const removeDataTrackListener = (track: RemoteDataTrack) => {
      const listener = dataTrackListeners.get(track);
      if (listener) {
        track.off('message', listener);
        dataTrackListeners.delete(track);
      }
    };

    const handleParticipant = (participant: RemoteParticipant) => {
      participant.dataTracks.forEach((publication: RemoteTrackPublication) => {
        if (publication.track) {
          handleDataTrack(publication.track as RemoteDataTrack);
        } else {
          publication.on('subscribed', track => handleDataTrack(track as RemoteDataTrack));
        }
      });

      participant.on('trackSubscribed', track => {
        if (track.kind === 'data') {
          handleDataTrack(track as RemoteDataTrack);
        }
      });
    };

    // Já conectados
    room.participants.forEach(handleParticipant);
    // Novos participantes
    room.on('participantConnected', handleParticipant);

    return () => {
      // Remove listener de participantes
      room.off('participantConnected', handleParticipant);
      // Remove todos listeners de dataTracks
      dataTrackListeners.forEach((listener, track) => {
        track.off('message', listener);
      });
      dataTrackListeners.clear();
    };
  }, [room, params]);
}
